/**
 * @file Spinner 加载指示
 * @description 相对父级绝对定位，覆盖父级区域；不占文档流，不影响兄弟节点。
 *
 * @whenToUse 父级已设 `relative` 的区块加载（表格、侧栏、卡片内容区）。
 * @whenNotToUse
 * 1. 包裹内容并带模糊蒙层（用 Spin）。
 * 2. 按钮内加载（用 Button 的 `loading`）。
 */

import { LoaderCircle } from 'lucide-react'
import { cn } from '@/lib/cn.ts'

export type SpinnerProps = {
  className?: string
  /** 根节点内联样式（可用于向 contentClassName 传入 CSS 变量） */
  style?: React.CSSProperties
  /** 无障碍文案 */
  label?: string
  /** 内容区域样式 */
  contentClassName?: string
}

/**
 * 覆盖父级的加载指示；父级需 `relative`。
 */
export function Spinner({ className, style, label = '加载中', contentClassName }: SpinnerProps) {
  return (
    <span
      role="status"
      aria-label={label}
      style={style}
      className={cn(
        'absolute inset-0 z-11 flex items-center justify-center bg-sidebar/70 backdrop-blur-sm text-primary',
        className,
      )}
    >
      <div className={cn('flex items-center justify-center flex-col', contentClassName)}>
        <LoaderCircle className="size-5 animate-spin" aria-hidden />
        {label ? <span className="text-sm text-muted">{label}</span> : null}
      </div>
    </span>
  )
}
